import React, { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { subscribePocketCampaigns } from '../../services/pocketCampaignService';
import { useAuth } from '../../contexts/AuthContext';

function formatMetricDate(ts) {
  if (!ts) return '—';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
}

function metricCell(n) {
  if (n == null || n === '') return '0';
  const x = Number(n);
  return Number.isFinite(x) ? String(x) : '0';
}

function propertyLabel(row, properties, t) {
  const denorm = row.propertyAddressLine && String(row.propertyAddressLine).trim();
  if (denorm) return denorm;
  const propertyId = row.propertyId;
  if (!propertyId) return '—';
  const p = properties.find((x) => x.id === propertyId);
  if (!p) return t('home.pocketCampaignPropertyMissing');
  const line = [p.address, p.city].filter(Boolean).join(', ');
  return line || t('home.pocketCampaignPropertyMissing');
}

/**
 * @param {object} props
 * @param {Array<{ id: string, propertyId?: string }>} props.properties
 * @param {(campaign: object) => void} props.onResubmit
 * @param {(key: string) => string} props.t
 */
export default function PocketCampaignsTab({ properties, onResubmit, t }) {
  const { currentUser } = useAuth();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    return subscribePocketCampaigns(currentUser.uid, setRows);
  }, [currentUser?.uid]);

  return (
    <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <Mail className="w-5 h-5 text-violet-600" />
        {t('home.pocketCampaignsTitle')}
      </h2>
      <p className="text-sm text-gray-600 mb-4">{t('home.pocketCampaignsIntro')}</p>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm py-2">{t('home.pocketCampaignsEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-gray-600 border-b border-gray-200">
                <th className="py-2 pr-3 font-medium">{t('home.pocketCampaignProperty')}</th>
                <th className="py-2 pr-3 font-medium">{t('shareModal.vipMetricsCampaign')}</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap">{t('shareModal.vipMetricsUpdated')}</th>
                <th className="py-2 pr-2 font-medium text-right tabular-nums">{t('shareModal.vipMetricsSent')}</th>
                <th className="py-2 pr-2 font-medium text-right tabular-nums">{t('shareModal.vipMetricsOpens')}</th>
                <th className="py-2 pr-2 font-medium text-right tabular-nums">{t('shareModal.vipMetricsClicks')}</th>
                <th className="py-2 pr-2 font-medium text-right tabular-nums">{t('shareModal.vipMetricsSignups')}</th>
                <th className="py-2 pr-2 font-medium text-right tabular-nums">{t('shareModal.vipMetricsInterests')}</th>
                <th className="py-2 pl-2 font-medium text-right">{t('home.pocketCampaignActions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="py-2 pr-3 text-gray-800 max-w-[200px]">
                    {propertyLabel(row, properties, t)}
                  </td>
                  <td className="py-2 pr-3 font-mono text-gray-700" title={row.id}>
                    …{String(row.id).slice(-10)}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{formatMetricDate(row.updatedAt)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{metricCell(row.emailsSent)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{metricCell(row.uniqueOpens)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{metricCell(row.uniqueClicks)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{metricCell(row.signupLinks)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{metricCell(row.interestRequests)}</td>
                  <td className="py-2 pl-2 text-right">
                    <button
                      type="button"
                      onClick={() => onResubmit?.(row)}
                      disabled={!row.propertyId}
                      className="text-violet-700 hover:text-violet-900 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('home.pocketCampaignResubmit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
